import React, { useEffect, useMemo, useState } from "react";
import {
  FaCloudUploadAlt,
  FaImages,
  FaPlus,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { toast } from "react-toastify";

import AdminDrawer from "./AdminDrawer";

import { uploadImage } from "../../../../../services/photoServices";

import {
  getPhotos,
  resolveMediaUrl,
} from "../utils/albumsPhotosUtils";

export default function PhotosManagerDrawer({
  isOpen,
  album,
  saving,
  onClose,
  onAddPhoto,
  onDeletePhoto,
}) {
  const [photos, setPhotos] = useState([]);
  const [newPhotoUrl, setNewPhotoUrl] = useState("");
  const [newPhotoDescription, setNewPhotoDescription] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setPhotos(getPhotos(album));
    setNewPhotoUrl("");
    setNewPhotoDescription("");
    setImageFile(null);
    setLocalPreview("");
    setUploading(false);
  }, [isOpen, album]);

  useEffect(() => {
    if (!imageFile) {
      setLocalPreview("");
      return undefined;
    }

    const url = URL.createObjectURL(imageFile);
    setLocalPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const preview = useMemo(() => {
    return localPreview || resolveMediaUrl(newPhotoUrl, "/images/asso_placeholder.webp");
  }, [localPreview, newPhotoUrl]);

  const uploadPhotoIfNeeded = async () => {
    if (!imageFile) return newPhotoUrl;

    try {
      setUploading(true);

      const imageData = new FormData();
      imageData.append("image", imageFile);

      const response = await uploadImage(imageData);

      const uploadedUrl =
        response?.filePath ||
        response?.url ||
        response?.path ||
        response?.photo_url ||
        response?.data?.filePath ||
        response?.data?.url ||
        "";

      if (!uploadedUrl) {
        throw new Error("Aucune URL retournée après upload.");
      }

      return uploadedUrl;
    } finally {
      setUploading(false);
    }
  };

  const handleAddPhoto = async (event) => {
    event.preventDefault();

    if (!album?.id) {
      toast.error("Album introuvable.");
      return;
    }

    if (!newPhotoUrl && !imageFile) {
      toast.error("Ajoutez une URL ou sélectionnez un fichier image.");
      return;
    }

    try {
      const finalPhotoUrl = await uploadPhotoIfNeeded();

      const addedPhoto = await onAddPhoto?.(album.id, {
        photo_url: finalPhotoUrl,
        description: newPhotoDescription,
      });

      if (addedPhoto) {
        setPhotos((prev) => [...prev, addedPhoto]);
      } else {
        setPhotos((prev) => [
          ...prev,
          {
            id: `tmp-${Date.now()}`,
            photo_url: finalPhotoUrl,
            description: newPhotoDescription,
            is_active: true,
          },
        ]);
      }

      setNewPhotoUrl("");
      setNewPhotoDescription("");
      setImageFile(null);
      setLocalPreview("");
    } catch (error) {
      console.error("Erreur upload photo :", error);
      toast.error("Erreur lors de l'ajout de la photo.");
    }
  };

  const handleDelete = async (photoId) => {
    const success = await onDeletePhoto?.(photoId);

    if (success) {
      setPhotos((prev) => prev.filter((photo) => photo.id !== photoId));
    }
  };

  return (
    <AdminDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Photos de l’album${album?.titre ? ` : ${album.titre}` : ""}`}
      subtitle="Ajoutez ou supprimez les photos de cet album."
      width="max-w-6xl"
    >
      <div className="flex min-h-full flex-col">
        <div className="flex-1 space-y-5 p-4 sm:p-6">
          <div className="grid gap-5 xl:grid-cols-[380px_1fr]">
            <form
              onSubmit={handleAddPhoto}
              className="rounded-[24px] border border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-slate-900/60"
            >
              <div className="mb-5 flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <FaPlus />
                </span>

                <div>
                  <h3 className="font-bold text-slate-950 dark:text-white">
                    Ajouter une photo
                  </h3>

                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    URL externe ou upload local.
                  </p>
                </div>
              </div>

              <div className="aspect-video overflow-hidden rounded-[20px] bg-slate-100 dark:bg-slate-950">
                <img
                  src={preview}
                  alt="Aperçu nouvelle photo"
                  className="h-full w-full object-cover"
                  onError={(event) => {
                    event.currentTarget.src = "/images/asso_placeholder.webp";
                  }}
                />
              </div>

              <div className="mt-4 space-y-3">
                <InputField
                  label="URL de la photo"
                  value={newPhotoUrl}
                  onChange={(event) => setNewPhotoUrl(event.target.value)}
                  placeholder="https://... ou /uploads/..."
                />

                <label className="flex cursor-pointer items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white px-4 py-4 text-sm font-bold text-slate-600 transition hover:border-primary hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
                  <FaCloudUploadAlt />
                  {imageFile ? imageFile.name : "Choisir un fichier"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) =>
                      setImageFile(event.target.files?.[0] || null)
                    }
                    className="hidden"
                  />
                </label>

                <div>
                  <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                    Description
                  </label>

                  <textarea
                    value={newPhotoDescription}
                    onChange={(event) =>
                      setNewPhotoDescription(event.target.value)
                    }
                    rows={4}
                    placeholder="Description ou légende de la photo..."
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
                  />
                </div>

                <button
                  type="submit"
                  disabled={saving || uploading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <FaSave />
                  {uploading ? "Upload..." : "Ajouter la photo"}
                </button>
              </div>
            </form>

            <div className="rounded-[24px] border border-slate-200 bg-white p-4 dark:border-white/10 dark:bg-slate-950/60">
              <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300">
                    <FaImages />
                  </span>

                  <div>
                    <h3 className="font-bold text-slate-950 dark:text-white">
                      Photos enregistrées
                    </h3>

                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {photos.length.toLocaleString("fr-FR")} photo
                      {photos.length > 1 ? "s" : ""} dans cet album.
                    </p>
                  </div>
                </div>
              </div>

              {photos.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500 dark:border-white/10 dark:bg-slate-900 dark:text-slate-400">
                  Aucune photo dans cet album pour le moment.
                </div>
              ) : (
                <div className="grid max-h-[70vh] gap-4 overflow-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
                  {photos.map((photo) => (
                    <PhotoCard
                      key={photo.id}
                      photo={photo}
                      onDelete={() => handleDelete(photo.id)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4 dark:border-white/10 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
          >
            <FaTimes />
            Fermer
          </button>
        </div>
      </div>
    </AdminDrawer>
  );
}

function PhotoCard({ photo, onDelete }) {
  return (
    <article className="group overflow-hidden rounded-[24px] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
      <div className="relative aspect-video overflow-hidden bg-slate-100 dark:bg-slate-950">
        <img
          src={resolveMediaUrl(photo.photo_url)}
          alt={photo.description || "Photo de l'album"}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          onError={(event) => {
            event.currentTarget.src = "/images/asso_placeholder.webp";
          }}
        />

        <button
          type="button"
          onClick={onDelete}
          className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-2xl bg-rose-600 text-white shadow-lg transition hover:bg-rose-700"
          title="Supprimer la photo"
        >
          <FaTrash />
        </button>
      </div>

      <div className="p-4">
        <p className="line-clamp-2 min-h-[40px] text-sm leading-5 text-slate-600 dark:text-slate-300">
          {photo.description || "Aucune description"}
        </p>
      </div>
    </article>
  );
}

function InputField({ label, value, onChange, placeholder = "" }) {
  return (
    <div>
      <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
        {label}
      </label>

      <input
        type="text"
        value={value ?? ""}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none transition focus:ring-2 focus:ring-primary/40 dark:border-white/10 dark:bg-slate-950 dark:text-white"
      />
    </div>
  );
}